import React from 'react';
import { useLazyQuery } from '@apollo/client';
import { COMPANY_BRANDING } from '../graphql/queries';
import { DEFAULT_COMPANY_BRANDING } from '../static/defaultCompanyBranding';
import { CompanyBranding } from '../models';

interface CompanyBrandingContextProps {
  companyBranding: CompanyBranding;
  setCompanyBranding: React.Dispatch<React.SetStateAction<CompanyBranding>>;
}

export const CompanyBrandingContext =
  React.createContext<CompanyBrandingContextProps>({
    companyBranding: null,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setCompanyBranding: () => {},
  });

interface CompanyBrandingProviderProps {
  children: React.ReactNode;
}

export function CompanybrandingContextProvider({
  children,
}: CompanyBrandingProviderProps) {
  const [companyBranding, setCompanyBranding] =
    React.useState<CompanyBranding>(null);

  const [getCompanyBranding] = useLazyQuery<{
    companyBranding: CompanyBranding;
  }>(COMPANY_BRANDING, { fetchPolicy: 'no-cache' });

  const setBrandingLocalStorage = async () => {
    try {
      const { data } = await getCompanyBranding({
        variables: {
          filter: { company: process.env.NEXT_PUBLIC_COMPANY_ID },
        },
      });

      const brandingData = data?.companyBranding
        ? data.companyBranding
        : DEFAULT_COMPANY_BRANDING;
      setCompanyBranding(brandingData);

      // Save to localStorage, with a week of expiration
      localStorage.setItem(
        'companyBranding',
        JSON.stringify({
          expiration: new Date().setDate(new Date().getDate() + 7),
          data: brandingData,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  const value = React.useMemo(
    () => ({ companyBranding, setCompanyBranding }),
    [companyBranding]
  );

  React.useEffect(() => {
    const localBranding = JSON.parse(localStorage.getItem('companyBranding'));

    const brandingValidation =
      process.env.NEXT_PUBLIC_COMPANY_ID === localBranding?.data?.company;

    const brandingExpiration = localBranding
      ? new Date().getTime() > localBranding.expiration
      : false;

    if (brandingValidation && !brandingExpiration) {
      setCompanyBranding(localBranding.data);
    }

    setBrandingLocalStorage();
  }, []);

  return (
    <CompanyBrandingContext.Provider value={value}>
      {children}
    </CompanyBrandingContext.Provider>
  );
}
